import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import get from 'lodash.get';

import BasePage from '../../components/common/basePageView';
import {
  MetricWidget,
  RevenueChart,
  TopItemsWidget,
  TimePeriodSelector
} from './components';
import { userRolesStrings } from '../../constants/roles';
import CurrencyHelper from '../../helpers/currencyHelper';
import { usePageTitle } from '../../hooks';

// API functions
import {
  fetchDailySnapshots,
  fetchCampaignSnapshots,
  fetchOrganizationSnapshots,
  fetchEventSnapshots,
  fetchCharityPlaceApplications,
} from './api/homeApi';

import styles from './HomePage.module.css';
import { Link } from 'react-router-dom';

interface HomePageProps {
  organizationId: number | null;
  isOrgAdmin: boolean;
  isSystemAdmin: boolean;
  firstName: string;
  organizationCurrency: string | null;
}

interface HomePageState {
  timePeriod: 'last7Days' | 'last30Days' | 'last90Days' | 'ytd' | 'customRange';
  customDateFrom: string | null;
  customDateTo: string | null;
  isLoading: boolean;
  dailyData: {
    current: any[];
    previous: any[];
    period: number;
  };
  topCampaigns: any[];
  topOrganizations: any[];
  topEvents: any[];
  charityPlaceApplications: any[];
  metricsData: {
    totalRevenue: number;
    totalDonations: number;
    totalPages: number;
    revenueChange: number;
    donationsChange: number;
    pagesChange: number;
    previousRevenue: number;
    previousDonations: number;
    previousPages: number;
  };
}

const HomePage: React.FC<HomePageProps> = ({
  organizationId,
  isOrgAdmin,
  isSystemAdmin,
  firstName,
  organizationCurrency
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to get time period from URL
  const getTimePeriodFromUrl = ():
    | 'last7Days'
    | 'last30Days'
    | 'last90Days'
    | 'ytd'
    | 'customRange' => {
    const urlParams = new URLSearchParams(location.search);
    const period = urlParams.get('period');

    // Validate and return the period, default to "last7Days"
    if (
      period === 'last30Days' ||
      period === 'last90Days' ||
      period === 'ytd' ||
      period === 'customRange'
    ) {
      return period;
    }
    return 'last7Days'; // Default period
  };

  // Helper function to get custom date range from URL
  const getCustomDatesFromUrl = (): {
    dateFrom: string | null;
    dateTo: string | null;
  } => {
    const urlParams = new URLSearchParams(location.search);
    return {
      dateFrom: urlParams.get('dateFrom'),
      dateTo: urlParams.get('dateTo')
    };
  };

  // Helper function to update URL with time period
  const updateUrlWithPeriod = (
    period: 'last7Days' | 'last30Days' | 'last90Days' | 'ytd' | 'customRange',
    dateFrom?: string,
    dateTo?: string
  ) => {
    const urlParams = new URLSearchParams(location.search);

    if (period === 'last7Days') {
      // Remove period param for default to keep URLs clean
      urlParams.delete('period');
      urlParams.delete('dateFrom');
      urlParams.delete('dateTo');
    } else {
      urlParams.set('period', period);

      // Handle custom date range parameters
      if (period === 'customRange' && dateFrom && dateTo) {
        urlParams.set('dateFrom', dateFrom);
        urlParams.set('dateTo', dateTo);
      } else {
        urlParams.delete('dateFrom');
        urlParams.delete('dateTo');
      }
    }

    const newSearch = urlParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

    // Update URL without causing a page refresh
    navigate(newUrl, { replace: true });
  };

  const [state, setState] = useState<HomePageState>(() => {
    const timePeriod = getTimePeriodFromUrl();
    const { dateFrom, dateTo } = getCustomDatesFromUrl();

    return {
      timePeriod,
      customDateFrom: timePeriod === 'customRange' ? dateFrom : null,
      customDateTo: timePeriod === 'customRange' ? dateTo : null,
      isLoading: true,
      dailyData: {
        current: [],
        previous: [],
        period: 7
      },
      topCampaigns: [],
      topOrganizations: [],
      topEvents: [],
      charityPlaceApplications: [],
      metricsData: {
        totalRevenue: 0,
        totalDonations: 0,
        totalPages: 0,
        revenueChange: 0,
        donationsChange: 0,
        pagesChange: 0,
        previousRevenue: 0,
        previousDonations: 0,
        previousPages: 0
      }
    };
  }); // Calculate date ranges based on time period
  const getDateRanges = (
    period: string,
    customDateFrom?: string,
    customDateTo?: string
  ) => {
    const today = moment();
    let actualDays: number;
    let currentDateFrom: string;
    let currentDateTo: string;

    switch (period) {
      case 'last7Days':
        actualDays = 7;
        currentDateFrom = today
          .clone()
          .subtract(actualDays, 'days')
          .format('YYYY-MM-DD');
        currentDateTo = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
        break;
      case 'last30Days':
        actualDays = 30;
        currentDateFrom = today
          .clone()
          .subtract(actualDays, 'days')
          .format('YYYY-MM-DD');
        currentDateTo = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
        break;
      case 'last90Days':
        actualDays = 90;
        currentDateFrom = today
          .clone()
          .subtract(actualDays, 'days')
          .format('YYYY-MM-DD');
        currentDateTo = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
        break;
      case 'ytd':
        currentDateFrom = today.clone().startOf('year').format('YYYY-MM-DD');
        currentDateTo = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
        actualDays = today.diff(today.clone().startOf('year'), 'days');
        break;
      case 'customRange':
        if (!customDateFrom || !customDateTo) {
          throw new Error(
            'Custom date range requires both dateFrom and dateTo'
          );
        }
        currentDateFrom = customDateFrom;
        currentDateTo = customDateTo;
        actualDays =
          moment(customDateTo).diff(moment(customDateFrom), 'days') + 1;
        break;
      default:
        actualDays = 7;
        currentDateFrom = today
          .clone()
          .subtract(actualDays, 'days')
          .format('YYYY-MM-DD');
        currentDateTo = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
    }

    // Calculate same period from one year ago
    const currentFromMoment = moment(currentDateFrom);
    const currentToMoment = moment(currentDateTo);
    const previousYearDateFrom = currentFromMoment
      .clone()
      .subtract(1, 'year')
      .format('YYYY-MM-DD');
    const previousYearDateTo = currentToMoment
      .clone()
      .subtract(1, 'year')
      .format('YYYY-MM-DD');

    return {
      current: {
        dateFrom: currentDateFrom,
        dateTo: currentDateTo,
        days: actualDays
      },
      previousYear: {
        dateFrom: previousYearDateFrom,
        dateTo: previousYearDateTo,
        days: actualDays
      }
    };
  };
  // Load data sequentially to avoid API overload
  const loadData = async (
    period: string,
    customDateFrom?: string,
    customDateTo?: string
  ) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const { current, previousYear } = getDateRanges(
        period,
        customDateFrom,
        customDateTo
      );
      const bracket = isSystemAdmin ? 'defaultOnline' : 'default';
      // Step 1: Load current period daily snapshots
      const currentDailyResponse = await fetchDailySnapshots(
        current.dateFrom,
        bracket,
        current.dateTo
      );
      const currentDailyData = get(currentDailyResponse, 'data.list', []);

      // Step 2: Load previous year period daily snapshots
      const previousYearDailyResponse = await fetchDailySnapshots(
        previousYear.dateFrom,
        bracket,
        previousYear.dateTo
      );
      const previousYearDailyData = get(
        previousYearDailyResponse,
        'data.list',
        []
      );

      // Step 3: Process daily data for metrics and chart
      const currentMetrics = calculateMetrics(currentDailyData);
      const previousYearMetrics = calculateMetrics(previousYearDailyData);

      const metricsData = {
        totalRevenue: currentMetrics.totalRevenue,
        totalDonations: currentMetrics.totalDonations,
        totalPages: currentMetrics.totalPages,
        revenueChange: calculatePercentageChange(
          currentMetrics.totalRevenue,
          previousYearMetrics.totalRevenue
        ),
        donationsChange: calculatePercentageChange(
          currentMetrics.totalDonations,
          previousYearMetrics.totalDonations
        ),
        pagesChange: calculatePercentageChange(
          currentMetrics.totalPages,
          previousYearMetrics.totalPages
        ),
        previousRevenue: previousYearMetrics.totalRevenue,
        previousDonations: previousYearMetrics.totalDonations,
        previousPages: previousYearMetrics.totalPages
      };

      // Step 4: Load top campaigns (using current period date)
      const campaignResponse = await fetchCampaignSnapshots(
        current.dateFrom,
        bracket
      );
      const topCampaigns = get(campaignResponse, 'data.list', []).slice(0, 5);

      // Step 5: Load charity place applications
      const charityPlaceResponse = await fetchCharityPlaceApplications();
      const charityPlaceApplications = get(
        charityPlaceResponse,
        'data.list',
        []
      ).slice(0, 5);

      let topOrganizations: any[] = [];
      let topEvents: any[] = [];

      // Step 6: Load additional data for SysAdmin (using current period date)
      if (isSystemAdmin) {
        try {
          const orgResponse = await fetchOrganizationSnapshots(
            current.dateFrom,
            bracket
          );
          const eventResponse = await fetchEventSnapshots(
            current.dateFrom,
            bracket
          );
          topOrganizations = get(orgResponse, 'data.list', []).slice(0, 5);
          topEvents = get(eventResponse, 'data.list', []).slice(0, 5);
        } catch (error) {
          console.warn('Error loading SysAdmin data:', error);
          // Continue without SysAdmin data
        }
      }

      setState((prev) => ({
        ...prev,
        dailyData: {
          current: currentDailyData,
          previous: previousYearDailyData,
          period: current.days
        },
        topCampaigns,
        topOrganizations,
        topEvents,
        charityPlaceApplications,
        metricsData,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error loading home page data:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Calculate metrics from daily data
  const calculateMetrics = (data: any[]) => {
    return data.reduce(
      (acc, day) => ({
        totalRevenue: acc.totalRevenue + (day.raisedAmount || 0),
        totalDonations: acc.totalDonations + (day.donationCount || 0),
        totalPages: acc.totalPages + (day.teamAndFundraiserCount || 0)
      }),
      { totalRevenue: 0, totalDonations: 0, totalPages: 0 }
    );
  };

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Handle time period change
  const handleTimePeriodChange = (
    period: 'last7Days' | 'last30Days' | 'last90Days' | 'ytd' | 'customRange',
    dateFrom?: string,
    dateTo?: string
  ) => {
    setState((prev) => ({
      ...prev,
      timePeriod: period,
      customDateFrom: dateFrom || null,
      customDateTo: dateTo || null
    }));
    updateUrlWithPeriod(period, dateFrom, dateTo);
    loadData(period, dateFrom, dateTo);
  };

  // Load initial data
  useEffect(() => {
    loadData(
      state.timePeriod,
      state.customDateFrom || undefined,
      state.customDateTo || undefined
    );
  }, []); // Only run on mount

  // Watch for URL parameter changes (back/forward navigation, bookmarks)
  useEffect(() => {
    const periodFromUrl = getTimePeriodFromUrl();
    const { dateFrom, dateTo } = getCustomDatesFromUrl();

    const hasTimePeriodChanged = periodFromUrl !== state.timePeriod;
    const hasCustomDatesChanged =
      periodFromUrl === 'customRange' &&
      (dateFrom !== state.customDateFrom || dateTo !== state.customDateTo);

    if (hasTimePeriodChanged || hasCustomDatesChanged) {
      setState((prev) => ({
        ...prev,
        timePeriod: periodFromUrl,
        customDateFrom: periodFromUrl === 'customRange' ? dateFrom : null,
        customDateTo: periodFromUrl === 'customRange' ? dateTo : null
      }));
      loadData(
        periodFromUrl,
        periodFromUrl === 'customRange' ? dateFrom || undefined : undefined,
        periodFromUrl === 'customRange' ? dateTo || undefined : undefined
      );
    }
  }, [location.search]); // Run when URL search params change

  const renderMetricWidgets = () => {
    const { metricsData } = state;

    // The organisation's own currency first, so a period with no donations still shows its
    // symbol rather than the AUD fallback. A system admin has no organisation, so falls
    // back to the data's currency.
    const currency =
      organizationCurrency || state.dailyData.current[0]?.currency || 'aud';
    // Get period display text for comparison (one year ago)
    const getPeriodText = () => {
      switch (state.timePeriod) {
        case 'last7Days':
          return 'same 7 days last year';
        case 'last30Days':
          return 'same 30 days last year';
        case 'last90Days':
          return 'same 90 days last year';
        case 'ytd':
          return 'same YTD period last year';
        case 'customRange':
          if (state.customDateFrom && state.customDateTo) {
            return `same period last year (${moment(state.customDateFrom).format('MMM D')} - ${moment(state.customDateTo).format('MMM D')})`;
          }
          return 'same period last year';
        default:
          return 'same period last year';
      }
    };
    const periodText = getPeriodText();

    return (
      <>
        <MetricWidget
          title={I18n.t('home.metrics.totalRevenue')}
          value={CurrencyHelper.formatCurrency(
            Math.round(metricsData.totalRevenue),
            currency,
            { notation: 'compact' }
          )}
          change={metricsData.revenueChange}
          previousValue={metricsData.previousRevenue}
          period={periodText}
          currency={currency}
          isCurrency={true}
          isLoading={state.isLoading}
        />
        <MetricWidget
          title={I18n.t('home.metrics.donationsReceived')}
          value={metricsData.totalDonations}
          change={metricsData.donationsChange}
          previousValue={metricsData.previousDonations}
          period={periodText}
          isLoading={state.isLoading}
        />
        <MetricWidget
          title={I18n.t('home.metrics.fundraisingPagesCreated')}
          value={metricsData.totalPages}
          change={metricsData.pagesChange}
          previousValue={metricsData.previousPages}
          period={periodText}
          isLoading={state.isLoading}
        />
      </>
    );
  };

  return (
    <BasePage>
      {/* TODO: Re-enable banner later - commented out for now */}
      {/* {isOrgAdmin && (
        <div className={styles.banner}>
          <h5 className="ui header">
            This page has been updated! Click <Link to={`/organizations/dashboard/${organizationId}`}>here</Link> to go to the legacy dashboard.
          </h5>
        </div>
      )} */}
      <div
        className={styles.homePage}
        style={{
          ...(isOrgAdmin && { marginTop: '2rem' })
        }}
      >
        {/* Header with greeting and time period selector */}
        <div className={styles.homeHeader}>
          <div className={styles.welcomeSection}>
            <h1 className={styles.welcomeTitle}>
              {I18n.t('general.greeting-home', { name: firstName })}
            </h1>
            <p className={styles.welcomeSubtitle}>
              {I18n.t('home.subtitle')}
              {isOrgAdmin && (
                <>
                  {'. '}Click{' '}
                  <Link to={`/organizations/dashboard/${organizationId}`}>
                    here
                  </Link>{' '}
                  to go to the legacy dashboard.
                </>
              )}
            </p>
          </div>
          <TimePeriodSelector
            selectedPeriod={state.timePeriod}
            customDateFrom={state.customDateFrom}
            customDateTo={state.customDateTo}
            onPeriodChange={handleTimePeriodChange}
          />
        </div>

        {/* Metric widgets */}
        <div className={styles.metricsRow}>{renderMetricWidgets()}</div>

        {/* Chart and Top Items Row */}
        <div className={styles.contentRow}>
          <div className={styles.chartSection}>
            <div className={styles.chartContainer}>
              <h3 className={styles.chartTitle}>
                {I18n.t('home.chart.revenueReport')}
              </h3>
              <RevenueChart
                data={state.dailyData}
                isLoading={state.isLoading}
                timePeriod={state.timePeriod}
              />
            </div>
          </div>

          <div className={styles.topItemsSection}>
            <TopItemsWidget
              title={I18n.t('home.topCampaigns.title')}
              items={state.topCampaigns}
              isLoading={state.isLoading}
              type="campaign"
              viewAllLink="/campaigns/live"
            />

            {isSystemAdmin && (
              <>
                <TopItemsWidget
                  title={I18n.t('home.topOrganizations.title')}
                  systemOnly
                  items={state.topOrganizations}
                  isLoading={state.isLoading}
                  type="organization"
                  viewAllLink="/organizations"
                />
                <TopItemsWidget
                  title={I18n.t('home.topEvents.title')}
                  systemOnly
                  items={state.topEvents}
                  isLoading={state.isLoading}
                  type="event"
                  viewAllLink="/events/live"
                />
              </>
            )}

            <TopItemsWidget
              title="Recent PlaceHub applications"
              items={state.charityPlaceApplications}
              isLoading={state.isLoading}
              type="application"
              viewAllLink="/vip-program/applications"
            />
          </div>
        </div>
      </div>
    </BasePage>
  );
};

const mapState = ({ session }: any) => {
  return {
    organizationId: session?.organization?.id || null,
    isOrgAdmin: session?.isOrganizationAdmin || false,
    isSystemAdmin: session?.isSystemAdmin || false,
    firstName: get(session, 'account.firstName', 'User'),
    organizationCurrency: session?.organization?.currency || null
  };
};

export default connect(mapState)(HomePage);
